import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ScrollArea } from '@/components/ui/scroll-area';
import { navItemsForRole } from '@/config/nav';
import { usePermission } from '@/core/permissions';
import { cn } from '@/lib/utils';

interface SidebarNavProps {
  /** Called after a link is clicked — used to close the mobile drawer. */
  onNavigate?: () => void;
}

/** The active item owns the longest prefix of the current path, counting each
 *  item's `to` plus its `activePrefixes` (deep routes like /lessons/… light up
 *  Курси); `/` matches only exactly so Dashboard isn't perpetually active. */
function pickActivePath(pathname: string, items: { to: string; activePrefixes?: string[] }[]): string | null {
  let best: string | null = null;
  let bestLength = -1;
  for (const item of items) {
    for (const candidate of [item.to, ...(item.activePrefixes ?? [])]) {
      const matches =
        candidate === '/'
          ? pathname === '/'
          : pathname === candidate || pathname.startsWith(candidate + '/');
      if (matches && candidate.length > bestLength) {
        best = item.to;
        bestLength = candidate.length;
      }
    }
  }
  return best;
}

export function SidebarNav({ onNavigate }: SidebarNavProps) {
  const { t } = useTranslation('nav');
  const { role } = usePermission();
  const items = navItemsForRole(role);
  const { pathname } = useLocation();
  const activeTo = pickActivePath(pathname, items);

  return (
    <ScrollArea className="h-full">
      <nav className="flex flex-col gap-1 p-3" aria-label={t('common:navigation')}>
        {items.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end
            onClick={onNavigate}
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              item.to === activeTo ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'
            )}
          >
            <item.icon className="size-4 shrink-0" />
            {t(item.labelKey)}
          </NavLink>
        ))}
      </nav>
    </ScrollArea>
  );
}
